import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  Alert,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useLanguage } from '@/contexts/LanguageContext';
import OnboardingLayout from '@/components/OnboardingLayout';
import { useOnboardingStepTracking } from '@/hooks/useOnboardingStepTracking';
import CircularArrowButton from '@/components/CircularArrowButton';
import { TempOnboardingService } from '@/services/tempOnboardingService';
import { OnboardingStateService } from '@/services/onboardingStateService';

export default function OnboardingNameScreen() {
  const { logCompleted } = useOnboardingStepTracking('name');
  const router = useRouter();
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const initializeOnboarding = async () => {
      await OnboardingStateService.setOnboardingInProgress(true);
      console.log('🚀 Onboarding flow started - flag set to prevent auto-redirect');
      
      const data = await TempOnboardingService.getData();
      if (data.name) {
        setName(data.name);
        console.log('📥 Loaded previously saved name:', data.name);
      }
    };
    initializeOnboarding();
  }, []);

  const handleContinue = async () => {
    if (!name.trim()) {
      Alert.alert(t('onboarding.name.required'), t('onboarding.name.requiredMessage'));
      return;
    }

    setLoading(true);

    try {
      await TempOnboardingService.saveName(name.trim());
      console.log('✅ Name saved to temporary storage (pre-auth flow):', name.trim());

      // PRD §5.2 — `onboarding_step_completed` for the `name` step. Hook
      // tracks duration_ms from mount automatically.
      logCompleted();

      router.push('/onboarding-interests');
    } catch (error) {
      console.error('❌ Error saving name:', error);
      Alert.alert(t('onboarding.name.error'), t('onboarding.name.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const progressWidth = 12; // Step 1 of 7

  return (
    <OnboardingLayout progressWidth={progressWidth}>
      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          <View style={styles.section}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepNumber}>1</Text>
            </View>

            <View style={styles.textContainer}>
              <Text style={styles.title}>{t('onboarding.name.title')}</Text>
              <Text style={styles.subtitle}>
                {t('onboarding.name.subtitle')}
              </Text>
            </View>
          </View>

          <View style={styles.fieldSection}>
            <View style={styles.field}>
              <TextInput
                style={[styles.input, { color: '#151515' }]}
                placeholder={t('onboarding.name.placeholder')}
                placeholderTextColor="#A19F97"
                value={name}
                onChangeText={setName}
                autoCapitalize="words"
                returnKeyType="next"
                onSubmitEditing={handleContinue}
              />
            </View>
          </View>
        </ScrollView>

        <View style={[styles.buttonContainer, { bottom: insets.bottom + 24 }]}>
          <CircularArrowButton
            onPress={handleContinue}
            disabled={loading || !name.trim()}
          />
        </View>
      </KeyboardAvoidingView>
    </OnboardingLayout>
  );
}

const styles = StyleSheet.create({
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  section: {
    padding: 24,
    paddingBottom: 0,
    alignItems: 'flex-start',
    gap: 24,
  },
  stepBadge: {
    backgroundColor: '#151515',
    borderRadius: 32,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumber: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    lineHeight: 28,
  },
  textContainer: {
    alignItems: 'flex-start',
    gap: 8,
  },
  title: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    width: 345,
  },
  subtitle: {
    color: '#56554D',
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    width: 345,
  },
  fieldSection: {
    padding: 24,
    alignItems: 'flex-start',
    gap: 12,
  },
  field: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 12,
  },
  input: {
    width: '100%',
    height: 48,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 20,
    color: '#151515',
  },
  buttonContainer: {
    position: 'absolute',
    right: 24,
  },
});
